"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useNetworkStatus } from "./useNetworkStatus";
import {
  syncAll,
  getPendingCount,
  getStuckCount,
  getLastSyncAt,
  setLastSyncAt,
  runClientMigrations,
  type SyncState,
  type SyncResult,
} from "@/core/offline/sync-engine";

const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const STALE_THRESHOLD_MS = 48 * 60 * 60 * 1000; // 48 heures

/**
 * Hook qui orchestre la synchronisation offline → serveur.
 * - Sync automatique au retour du réseau
 * - Sync périodique toutes les 5 min si online
 * - Expose l'état pour l'UI (indicateur sync dans le header)
 */
export function useSyncEngine() {
  const { isOnline } = useNetworkStatus();
  const [syncState, setSyncState] = useState<SyncState>(
    typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "idle"
  );
  const [pendingCount, setPendingCount] = useState(0);
  // Entités bloquées (error > cap retries C5) : à signaler distinctement du pending.
  const [stuckCount, setStuckCount] = useState(0);
  const [lastSyncAt, setLastSyncAtState] = useState<Date | null>(null);
  const [isStale, setIsStale] = useState(false);
  const isSyncing = useRef(false);

  // Migrations client (one-shot) + charger la date de dernière sync au montage.
  // Les migrations doivent s'exécuter AVANT la lecture du lastSyncAt : certaines
  // d'entre elles effacent la clé pour forcer un full pull au prochain sync.
  useEffect(() => {
    runClientMigrations();
    setLastSyncAtState(getLastSyncAt());
  }, []);

  // Vérifier si les données sont "stale" (>48h sans sync)
  useEffect(() => {
    if (!lastSyncAt) {
      // Pas encore de sync → stale si des données pending existent
      setIsStale(pendingCount > 0);
      return;
    }
    const elapsed = Date.now() - lastSyncAt.getTime();
    setIsStale(elapsed > STALE_THRESHOLD_MS && pendingCount > 0);
  }, [lastSyncAt, pendingCount]);

  // Mettre à jour les compteurs pending + bloquées
  const refreshPendingCount = useCallback(async () => {
    try {
      const [count, stuck] = await Promise.all([
        getPendingCount(),
        getStuckCount(),
      ]);
      setPendingCount(count);
      setStuckCount(stuck);
    } catch {
      // Ignorer les erreurs de lecture Dexie
    }
  }, []);

  // Fonction de sync principale
  const doSync = useCallback(async (): Promise<SyncResult | null> => {
    if (isSyncing.current) return null;
    if (!navigator.onLine) {
      setSyncState("offline");
      return null;
    }

    isSyncing.current = true;
    setSyncState("syncing");

    try {
      const result = await syncAll();

      if (result.success) {
        // Utiliser le timestamp serveur pour éviter le décalage d'horloge client/serveur
        const syncDate = result.serverTime ? new Date(result.serverTime) : new Date();
        setLastSyncAt(syncDate);
        setLastSyncAtState(syncDate);
        setSyncState("idle");
      } else {
        setSyncState("error");
      }

      await refreshPendingCount();
      return result;
    } catch (err) {
      console.error("[SyncEngine] Erreur sync:", err);
      setSyncState("error");
      return null;
    } finally {
      isSyncing.current = false;
    }
  }, [refreshPendingCount]);

  // Sync automatique au retour online
  useEffect(() => {
    if (isOnline) {
      setSyncState("idle");
      // Petit délai pour laisser le réseau se stabiliser
      const timer = setTimeout(() => {
        doSync();
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setSyncState("offline");
    }
  }, [isOnline, doSync]);

  // Sync périodique toutes les 5 min
  useEffect(() => {
    if (!isOnline) return;

    const interval = setInterval(() => {
      doSync();
    }, SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOnline, doSync]);

  // Rafraîchir le compteur au montage et régulièrement
  useEffect(() => {
    refreshPendingCount();
    const interval = setInterval(refreshPendingCount, 30_000); // Toutes les 30s
    return () => clearInterval(interval);
  }, [refreshPendingCount]);

  // Fonction manuelle "Forcer la sync"
  const forceSync = useCallback(async () => {
    return doSync();
  }, [doSync]);

  return {
    syncState,
    pendingCount,
    stuckCount,
    lastSyncAt,
    isStale,
    isOnline,
    forceSync,
  };
}
